// In Box.tsx
import {
  BoxProps,
  boxRestyleFunctions,
  createRestyleComponent,
  createRestyleFunction,
  ResponsiveValue,
} from "@shopify/restyle";
import React, { forwardRef, ForwardedRef } from "react";
import { View, ViewProps, ViewStyle } from "react-native";
import {
  Edges,
  NativeSafeAreaViewInstance,
  SafeAreaView,
} from "react-native-safe-area-context";

import { Theme } from "./../../../theme/restyle/theme";

type SafeAreaBoxProps = ViewProps & {
  safeArea?: boolean;
  safeAreaTop?: boolean;
  webOverflowY?: "auto" | "hidden" | "scroll" | "visible";
};

type WebViewStyle = ViewStyle & {
  overflowY?: "auto" | "hidden" | "scroll" | "visible";
};

type WebPositionProps = {
  webPosition?: ResponsiveValue<"fixed" | "sticky", Theme["breakpoints"]>;
};

type WhooshBoxProps = BoxProps<Theme> & SafeAreaBoxProps & WebPositionProps;
type BoxRef = View | NativeSafeAreaViewInstance;

const allSafeAreaEdges: Edges = ["top", "right", "bottom", "left"];
const topSafeAreaEdge: Edges = ["top"];

const webPosition = createRestyleFunction<Theme, WebPositionProps>({
  property: "webPosition",
  styleProperty: "position",
});

const updateForwardedRef = (
  ref: ForwardedRef<BoxRef>,
  value: BoxRef | null
) => {
  if (typeof ref === "function") {
    ref(value);
  } else if (ref) {
    ref.current = value;
  }
};

const SafeAreaBoxBase = forwardRef<BoxRef, SafeAreaBoxProps>(
  ({ safeArea, safeAreaTop, style, webOverflowY, ...props }, ref) => {
    const compatibilityStyle: WebViewStyle | undefined = webOverflowY
      ? {
          // eslint-disable-next-line no-restricted-syntax -- Compatibility for migrated NativeBase Boxes rendered by the web-only staff app.
          overflowY: webOverflowY,
        }
      : undefined;
    const resolvedStyle = compatibilityStyle
      ? [compatibilityStyle, style]
      : style;
    const setRef = (value: BoxRef | null) => updateForwardedRef(ref, value);

    if (safeArea || safeAreaTop) {
      return (
        <SafeAreaView
          ref={setRef}
          {...props}
          edges={safeArea ? allSafeAreaEdges : topSafeAreaEdge}
          style={resolvedStyle}
        />
      );
    }

    return <View ref={setRef} {...props} style={resolvedStyle} />;
  }
);

SafeAreaBoxBase.displayName = "SafeAreaBoxBase";

export const Box = createRestyleComponent<WhooshBoxProps, Theme>(
  [...boxRestyleFunctions, webPosition],
  SafeAreaBoxBase
);
